import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarClock, CheckCircle2, XCircle, RefreshCw, Building2, AlertTriangle } from "lucide-react";
import { api, formatApiError, type PublicAppointment, type AvailabilitySlot } from "@/lib/api";

export default function AppointmentManage() {
  const { token = "" } = useParams();
  const [appt, setAppt] = useState<PublicAppointment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"view" | "reschedule">("view");
  const [rDate, setRDate] = useState("");
  const [slots, setSlots] = useState<AvailabilitySlot[] | null>(null);
  const [slot, setSlot] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setAppt(await api.publicApptGet(token)); }
    catch (e) { setError(formatApiError(e)); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setBusy(true);
    try { setAppt(await api.publicApptConfirm(token)); setMsg("Thanks! Your appointment is confirmed."); }
    catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this appointment?")) return;
    setBusy(true);
    try { setAppt(await api.publicApptCancel(token)); setMsg("Your appointment has been cancelled."); }
    catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };
  const loadSlots = async (d: string) => {
    setRDate(d); setSlot(""); setSlots(null);
    if (!d) return;
    try { const r = await api.publicApptAvailability(token, d); setSlots(r.slots); }
    catch (e) { setError(formatApiError(e)); }
  };
  const doReschedule = async () => {
    if (!slot) return;
    setBusy(true);
    try { setAppt(await api.publicApptReschedule(token, slot)); setMode("view"); setMsg("Your appointment has been rescheduled and confirmed."); }
    catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };

  const statusTone: Record<string, string> = {
    confirmed: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800",
    cancelled: "bg-slate-200 text-slate-600", rescheduled: "bg-slate-200 text-slate-600",
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10" data-testid="appt-manage-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
        {error && !appt ? (
          <div className="text-center" data-testid="appt-error">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-500" />
            <p className="text-slate-700">{error}</p>
          </div>
        ) : !appt ? (
          <p className="text-center text-slate-400">Loading…</p>
        ) : (
          <>
            <div className="mt-4 flex items-center gap-2 text-slate-500"><Building2 className="h-4 w-4" /><span className="text-sm font-medium">{appt.business_name}</span></div>
            <h1 className="font-display text-2xl font-semibold text-slate-900">{appt.is_no_show ? "We missed you" : "Your appointment"}</h1>
            {appt.is_no_show && <p className="mt-1 text-sm text-slate-600" data-testid="appt-noshow-note">Sorry we missed you today — you can pick a new time below.</p>}
            <div className="mt-4 rounded-xl bg-slate-50 p-4" data-testid="appt-summary">
              <div className="flex items-start gap-3">
                <CalendarClock className="mt-0.5 h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-semibold text-slate-900">{appt.service ?? "Appointment"}</p>
                  <p className="text-sm text-slate-600">{appt.date_display} · {appt.time_display}</p>
                  <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${statusTone[appt.status] ?? "bg-slate-100 text-slate-600"}`} data-testid="appt-status">{appt.status.replace(/_/g, " ")}</span>
                </div>
              </div>
            </div>

            {msg && <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="appt-message">{msg}</p>}
            {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            {appt.can_manage && mode === "view" && (
              <div className="mt-5 grid grid-cols-3 gap-2" data-testid="appt-actions">
                {appt.can_confirm && <button onClick={confirm} disabled={busy || appt.status === "confirmed"} data-testid="appt-confirm-btn"
                  className="flex flex-col items-center gap-1 rounded-xl border border-emerald-200 bg-emerald-50 py-3 text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50">
                  <CheckCircle2 className="h-5 w-5" /><span className="text-xs font-semibold">Confirm</span></button>}
                {appt.can_reschedule && <button onClick={() => { setMode("reschedule"); loadSlots(""); }} disabled={busy} data-testid="appt-reschedule-btn"
                  className="flex flex-col items-center gap-1 rounded-xl border border-blue-200 bg-blue-50 py-3 text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50">
                  <RefreshCw className="h-5 w-5" /><span className="text-xs font-semibold">Reschedule</span></button>}
                {appt.can_cancel && <button onClick={cancel} disabled={busy} data-testid="appt-cancel-btn"
                  className="flex flex-col items-center gap-1 rounded-xl border border-red-200 bg-red-50 py-3 text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50">
                  <XCircle className="h-5 w-5" /><span className="text-xs font-semibold">Cancel</span></button>}
              </div>
            )}

            {mode === "reschedule" && (
              <div className="mt-5 space-y-3" data-testid="appt-reschedule-panel">
                <p className="text-sm font-semibold text-slate-800">Pick a new time</p>
                <input type="date" value={rDate} onChange={(e) => loadSlots(e.target.value)} data-testid="appt-reschedule-date"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                {slots === null ? (rDate ? <p className="text-sm text-slate-400">Loading times…</p> : null)
                  : slots.length === 0 ? <p className="text-sm text-slate-500" data-testid="appt-no-slots">No open times on this day. Try another date.</p>
                  : (
                    <div className="flex flex-wrap gap-2">
                      {slots.map((s) => (
                        <button key={s.start_at} onClick={() => setSlot(s.start_at)} data-testid={`appt-slot-${s.local_time}`}
                          className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${slot === s.start_at ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}>
                          {s.local_time}
                        </button>
                      ))}
                    </div>
                  )}
                <div className="flex justify-end gap-2">
                  <button onClick={() => setMode("view")} className="rounded-lg px-3 py-2 text-sm text-slate-500">Back</button>
                  <button onClick={doReschedule} disabled={busy || !slot} data-testid="appt-reschedule-confirm"
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirm new time</button>
                </div>
              </div>
            )}

            {!appt.can_manage && <p className="mt-5 text-center text-sm text-slate-500">This appointment is {appt.status.replace(/_/g, " ")} and can no longer be changed here.</p>}
          </>
        )}
      </div>
    </div>
  );
}
